import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import type { CompanyPortalConfig } from "@/config/companies";
import { SponsorLogos } from "./SponsorLogos";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/ui/password-input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import youngIndiansLogo from "@/assets/young-indians-logo.png";

interface CompanyLoginProps {
  config: CompanyPortalConfig;
}

export function CompanyLogin({ config }: CompanyLoginProps) {
  const navigate = useNavigate();
  const { signIn } = useAuth();
  const { toast } = useToast();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const gradientStyle = {
    background: `linear-gradient(to bottom right, ${config.branding.gradientStart}, ${config.branding.gradientEnd})`,
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email || !password) {
      toast({
        title: "Missing fields",
        description: "Please enter both email and code",
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);
    try {
      await signIn(email, password);
      navigate(config.routes.app);
    } catch (error: any) {
      console.error("Login error:", error);
      toast({
        title: "Login failed",
        description: error.message || "Invalid email or code. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div 
      className="min-h-screen w-full flex flex-col items-center justify-center p-4"
      style={gradientStyle}
    >
      {/* Company Logo */}
      {config.branding.logo ? (
        <img 
          src={config.branding.logo} 
          alt={config.name}
          className="h-24 w-auto mb-12 object-contain"
        />
      ) : (
        <h1 className="text-white text-4xl font-light mb-12">
          {config.name}
        </h1>
      )}

      {/* Login Card */}
      <div className="bg-white rounded-lg shadow-xl p-8 w-full max-w-md mb-8">
        <h2 className="text-center text-gray-800 text-xl font-semibold mb-6">
          Welcome to {config.name}
        </h2>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="email" className="text-gray-700">Email</Label>
            <Input
              id="email"
              type="email"
              placeholder="Enter your email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={isLoading}
              className="w-full"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="password" className="text-gray-700">Password</Label>
            <PasswordInput
              id="password"
              placeholder="Innoventure Roll Number"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={isLoading}
              className="w-full"
            />
          </div>

          <Button
            type="submit"
            disabled={isLoading}
            className="w-full"
            style={{ backgroundColor: config.branding.buttonColor || config.branding.primaryColor }}
          >
            {isLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Signing in...
              </>
            ) : (
              "Sign In"
            )}
          </Button>
        </form>
      </div>

      {/* Sponsor Logos Section */}
      <SponsorLogos
        logos={[
          'https://sekiwtiutnikmdbpiufi.supabase.co/storage/v1/object/public/siteassets/Sponsor%20Logo%203.jpeg',
          'https://sekiwtiutnikmdbpiufi.supabase.co/storage/v1/object/public/siteassets/Sponsor%20Logo%205.jpeg',
          youngIndiansLogo,
          'https://sekiwtiutnikmdbpiufi.supabase.co/storage/v1/object/public/siteassets/Sponsor%20Logo%202.jpeg',
          'https://sekiwtiutnikmdbpiufi.supabase.co/storage/v1/object/public/siteassets/Sponsor%20Logo%204.jpeg',
        ]}
        title="Supported by"
      />

      {/* Footer - Powered by Testera - Fixed at bottom */}
      <div className="fixed bottom-0 left-0 right-0 border-t border-white/20 bg-white/10 backdrop-blur-sm z-10">
        <div className="max-w-7xl mx-auto px-6 py-3">
          <p className="text-center text-sm text-white flex items-center justify-center gap-2">
            Powered by <img src="/lovable-uploads/e7e0cd62-757f-4e57-b3bf-42cffdc8d843.png" alt="Testera" className="h-4 w-auto" /> <span className="font-medium text-white">Testera</span>
          </p>
        </div>
      </div>
    </div>
  );
}
